import {authorized, optionallyAuthorized} from "./ApiClient";
import {send} from "./http";
import {LicenseViewModel, PresignedUrl, SupportTicketCategory, SupportTicketState, SupportTicketSummaryViewModel, SupportTicketViewModel} from "./models";
import {uploadToPresignedUrl} from "./upload";

export interface SupportUploadProgress {
    file: File;
    // 0–1
    fraction: number;
}

// A signed-out visitor's private link to their ticket (the ?access= of the link the API emailed them), by ticket: set by
// SupportTicketPage while it shows that ticket, and sent with every call about it (the X-Support-Access header) instead
// of an access token.
const visitorAccess = new Map<string, string>();

export function setVisitorAccess(ticketId: string, access: string | undefined): void {
    if (access) {
        visitorAccess.set(ticketId, access);
    } else {
        visitorAccess.delete(ticketId);
    }
}

// As the signed-in user, or for ticketId, as the visitor holding its link.
function call<T>(method: string, path: string, body: unknown, ticketId?: string): Promise<T> {
    const access = ticketId ? visitorAccess.get(ticketId) : undefined;
    return access ? send<T>(method, path, body, undefined, { headers: { 'X-Support-Access': access } }) : authorized<T>(method, path, body);
}

// Uploads each file to S3 (see components/supportFiles.ts for what's accepted) and returns their upload tokens, for the
// message that carries them. The API checks the files when the message is sent: 400 if one isn't what it claims to be.
async function uploadAttachments(files: File[], onProgress?: (progress: SupportUploadProgress) => void, ticketId?: string): Promise<string[]> {
    const tokens: string[] = [];
    for (const file of files) {
        const {uploadToken, upload} = await call<{ uploadToken: string, upload: PresignedUrl }>(
            'POST', '/support-ticket/attachment-upload', { fileName: file.name, contentType: file.type, size: file.size }, ticketId);
        await uploadToPresignedUrl(upload, file, fraction => onProgress?.({ file, fraction }));
        tokens.push(uploadToken);
    }
    return tokens;
}

const ticketPath = (ticketId: string) => `/support-ticket/${encodeURIComponent(ticketId)}`;

// Support tickets: the signed-in user's own, or (administrators, all: true) everyone's; and a signed-out visitor's one,
// through their emailed link (setVisitorAccess).
export const SupportApi = {
    getAll: (options?: { all?: boolean, state?: SupportTicketState }) => {
        const query = new URLSearchParams();
        if (options?.all) {
            query.set('scope', 'all');
        }
        if (options?.state) {
            query.set('state', options.state);
        }
        const text = query.toString();
        return authorized<SupportTicketSummaryViewModel[]>('GET', `/support-ticket${text ? `?${text}` : ''}`);
    },

    // 404 for a ticket that doesn't exist, or isn't the user's (or the visitor's, with its link).
    get: (ticketId: string) => call<SupportTicketViewModel>('GET', ticketPath(ticketId), undefined, ticketId),

    // Signed in: a ticket about one of the user's licenses (403 when it isn't theirs, or has been revoked), or with no
    // licenseId, a general one; returns it. Signed out: name and email instead, and no files; returns only its id (the
    // visitor sees it through the link the API emails them). Every new ticket needs a Turnstile token (400
    // turnstile_failed: do the check again).
    // A press inquiry (/press/contact) adds category 'press' and its details: no license, perhaps a product, and with
    // reviewLicense, the product, outletUrl and role.
    open: async (model: {
                     licenseId?: string, name?: string, email?: string, subject: string, body: string, turnstileToken: string,
                     category?: SupportTicketCategory, productId?: string, publication?: string, reviewLicense?: boolean,
                     outletUrl?: string, role?: string
                 }, files: File[],
                 onProgress?: (progress: SupportUploadProgress) => void) => {
        const attachments = await uploadAttachments(files, onProgress);
        return await optionallyAuthorized<SupportTicketViewModel | { id: string }>('POST', '/support-ticket', { ...model, attachments });
    },

    // 403 when the customer can no longer reply (the ticket is finished, or the license revoked).
    reply: async (ticketId: string, body: string, files: File[], onProgress?: (progress: SupportUploadProgress) => void) => {
        const attachments = await uploadAttachments(files, onProgress, ticketId);
        return await call<SupportTicketViewModel>('POST', `${ticketPath(ticketId)}/message`, { body, attachments }, ticketId);
    },

    changeState: (ticketId: string, state: SupportTicketState, reason: string) =>
        call<SupportTicketViewModel>('PUT', `${ticketPath(ticketId)}/state`, { state, reason }, ticketId),

    // Administrators only. Deletes the attachments too.
    delete: (ticketId: string) => authorized<void>('DELETE', ticketPath(ticketId)),

    // Administrators, on a press inquiry: issues its review license for its product, to the inquiry's account (made if
    // need be), notes it on the ticket, resolves it and emails the reviewer the key. 409 review_license_issued.
    issueReviewLicense: (ticketId: string) => authorized<LicenseViewModel>('POST', `${ticketPath(ticketId)}/review-license`),

    // A pre-signed URL (valid a few minutes) that downloads the file.
    downloadUrl: (ticketId: string, attachmentId: string) =>
        call<PresignedUrl>('GET', `${ticketPath(ticketId)}/attachment/${encodeURIComponent(attachmentId)}`, undefined, ticketId)
};
